import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button } from 'react-bootstrap';
import Select from 'react-dropdown-select';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import WhatsAppTemplatePreview from '../../../components/WhatsApp/TemplatePreview';
import { getRequest, postRequest } from '../../../helpers/apiHandler';
import { applyTextFormat } from '../../../helpers/textFormatHelper';
import useFBAuthenticated from '../../../hooks/useFBAuthenicated';
import { showAlert } from '../../../store/actions/alertAction';

const WhatsAppTemplateEditor = () => {
  const { waba_id, phone_number_id, template_id } = useParams();
  const { access_token } = useSelector((state) => state.auth.user);
  const isFBAuthenticated = useFBAuthenticated(access_token);
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [template, setTemplate] = useState({
    name: '',
    language: '',
    category: '',
    sub_category: '',
    components: [],
  });
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [templateHeader, setTemplateHeader] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateFooter, setTemplateFooter] = useState('');
  const [templateButtons, setTemplateButtons] = useState([]);

  const [bodyVariables, setBodyVariables] = useState([]);
  const [bodyVariableEntries, setBodyVariableEntries] = useState([]);

  const [codeExp, setCodeExp] = useState(0);

  const bodyTextareaRef = useRef(null);
  const navigate = useNavigate();

  const fetchTemplateDetails = () => {
    postRequest('/v2/whatsapp/wa-template-details', { template_id }, access_token.token, (res) => {
      if (res.success) {
        const { template } = res.data;
        const components = template.components || [];

        const componentData = components.reduce(
          (extractedData, component) => {
            switch (component.type) {
              case 'HEADER':
                extractedData.header = component.text || '';
                break;
              case 'BODY':
                extractedData.body = component.text || '';
                break;
              case 'FOOTER':
                extractedData.footer = component.text || '';
                break;
              case 'BUTTONS':
                extractedData.buttons = component.buttons || [];
                break;
            }
            return extractedData;
          },
          { header: '', body: '', footer: '', buttons: [] }
        );

        setTemplate(template);
        setTemplateHeader(componentData.header);
        setTemplateBody(componentData.body);
        setTemplateFooter(componentData.footer);
        setTemplateButtons(componentData.buttons);

        if (template.category === 'AUTHENTICATION' && componentData.footer) {
          const match = componentData.footer.match(/\d+/);
          if (match) {
            setCodeExp(parseInt(match[0]));
          }
        }
      }
    });
  };

  const fetchTemplateLanguages = () => {
    const requestData = { headers: { Authorization: access_token.token } };
    getRequest('/v2/whatsapp/wa-template-languages', requestData, (res) => {
      if (res.success) {
        const { languages } = res.data;
        setLanguages(languages);
      }
    });
  };

  useEffect(() => {
    if (isFBAuthenticated && !isLoaded) {
      fetchTemplateLanguages();
      fetchTemplateDetails();
      setIsLoaded(true);
    }
  }, [isFBAuthenticated, isLoaded, setIsLoaded]);

  useEffect(() => {
    if (template.language !== '' && languages.length > 0) {
      const foundLanguage = languages.find((lang) => lang.value === template.language);
      if (foundLanguage) {
        setSelectedLanguage([foundLanguage]);
      }
    }
  }, [template.language, languages]);

  const detectVariables = (text) => {
    const variablePattern = /\{\{\d+\}\}/g;
    return text.match(variablePattern) || [];
  };

  useEffect(() => {
    setBodyVariables(detectVariables(templateBody));
  }, [templateBody]);

  const handleInputChange = (e, field) => {
    setTemplate({
      ...template,
      [field]: e.target.value,
    });
  };

  const handleLanguageChange = (opt) => {
    setTemplate({
      ...template,
      language: opt[0].value,
    });
  };

  const handleSubmit = () => {
    let requestData = {};
    if (template.category === 'AUTHENTICATION') {
      requestData = {
        template_id,
        name: template.name,
        language: selectedLanguage[0].value,
        category: template.category,
        components: [
          templateBody && {
            type: 'BODY',
            text: templateBody,
            add_security_recommendation: true,
          },
          {
            type: 'FOOTER',
            text: `Your code will expire in ${codeExp} minutes`,
            code_expiration_minutes: codeExp,
          },
          templateButtons.length > 0 && {
            type: 'BUTTONS',
            buttons: templateButtons,
          },
        ].filter(Boolean),
      };
    } else {
      requestData = {
        template_id,
        name: template.name,
        language: selectedLanguage[0].value,
        category: template.category,
        components: [
          templateHeader && {
            type: 'HEADER',
            format: 'TEXT',
            text: templateHeader,
          },
          templateBody && {
            type: 'BODY',
            text: templateBody,
            ...(bodyVariables.length > 0 && {
              example: {
                body_text: bodyVariableEntries.map((entry) => [entry]),
              },
            }),
          },
          templateFooter && { type: 'FOOTER', text: templateFooter },
          templateButtons.length > 0 && {
            type: 'BUTTONS',
            buttons: templateButtons.map((button) => ({
              type: button.type,
              text: button.text,
              ...(button.type === 'URL' && { url: button.url }),
              ...(button.type === 'PHONE_NUMBER' && { phone_number: button.phoneNumber }),
            })),
          },
        ].filter(Boolean),
      };
    }
    console.log(requestData);
    postRequest('/v2/whatsapp/wa-template-update', requestData, access_token.token, (res) => {
      if (res.success) {
        dispatch(showAlert(res.message, 'success'));
        navigate(`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`);
      } else {
        dispatch(showAlert(res.message, 'danger'));
      }
    });
  };

  const handleAddVariable = useCallback(() => {
    const newVariableNumber = bodyVariables.length + 1;
    const newVariable = `{{${newVariableNumber}}}`;
    setBodyVariables([...bodyVariables, newVariable]);
    setTemplateBody(`${templateBody}${newVariable}`);
  }, [bodyVariables, templateBody]);

  const handleRemoveVariable = useCallback(
    (index) => {
      const newBodyVariables = bodyVariables.filter((_, i) => i !== index);
      setBodyVariables(newBodyVariables);

      const newTemplateBody = templateBody.replace(`{{${index + 1}}}`, '');
      setTemplateBody(newTemplateBody);
    },
    [bodyVariables, templateBody]
  );

  const handleBodyVariableInputChange = useCallback((index, value) => {
    setBodyVariableEntries((prevEntries) => {
      const newEntries = [...prevEntries];
      newEntries[index] = value;
      return newEntries;
    });
  }, []);

  const handleButtonTextChange = useCallback((index, text) => {
    setTemplateButtons((prevButtons) => {
      const newButtons = [...prevButtons];
      newButtons[index].text = text;
      return newButtons;
    });
  }, []);

  const handleTextFormat = useCallback(
    (formatType) => {
      const textarea = bodyTextareaRef.current;
      if (!textarea) return;

      const result = applyTextFormat({
        text: templateBody,
        start: textarea.selectionStart,
        end: textarea.selectionEnd,
        formatType,
      });

      if (result) {
        setTemplateBody(result.text);

        // Restore selection
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
        }, 0);
      }
    },
    [templateBody]
  );

  return (
    <div className="container-fluid p-4">
      <div className="row">
        {/* Left Editor Panel */}
        <div className="col-md-8">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center bg-white">
              <div className="d-flex align-items-center">
                <div className="bg-success text-white p-2 rounded mr-2">
                  <i className="fas fa-comment"></i>
                </div>
                <div>
                  <h6 className="mb-0">
                    {template.name} • {template.language}
                  </h6>
                  <small className="text-muted">
                    {template.category} • {template.sub_category}
                  </small>
                </div>
              </div>
              <Button variant="outline-secondary" className="btn-sm">
                Delete Template
              </Button>
            </div>

            <div className="card-body">
              {/* Template Name and Language */}
              <div className="mb-2">
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label className="description">Template Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name your template"
                        value={template.name}
                        onChange={(e) => handleInputChange(e, 'name')}
                        disabled={true}
                      />
                      <small className="text-muted">{template.name.length}/512</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="description">Language</label>
                    <Select
                      values={selectedLanguage}
                      onChange={handleLanguageChange}
                      options={languages}
                      placeholder="Select a language"
                    />
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="mb-2">
                <h6 className="text-dark">Template Content</h6>
                <p className="text-muted small">
                  Fill out the provided form fields for your template.
                </p>

                {/* Header */}
                {template.category !== 'AUTHENTICATION' && (
                  <div className="form-group">
                    <label className="d-flex justify-content-between">
                      <span>
                        Header <span className="text-muted">• Optional</span>
                      </span>
                      <small className="text-muted">{templateHeader.length}/60</small>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter header text"
                      value={templateHeader}
                      onChange={(e) => {
                        setTemplateHeader(e.target.value);
                      }}
                    />
                  </div>
                )}

                {/* Body */}
                <div className="form-group">
                  <label className="d-flex justify-content-between">
                    <span>Body</span>
                    <small className="text-muted">{templateBody.length}/1024</small>
                  </label>
                  <textarea
                    ref={bodyTextareaRef}
                    className="form-control"
                    rows="3"
                    name="body"
                    placeholder="Enter body text"
                    value={templateBody}
                    onChange={(e) => setTemplateBody(e.target.value)}
                  ></textarea>
                  <div className="mt-2 btn-toolbar">
                    <button className="btn btn-sm btn-light mr-1">😊</button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light mr-1"
                      onClick={() => handleTextFormat('bold')}
                      title="Make text bold (*text*)"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light mr-1"
                      onClick={() => handleTextFormat('italic')}
                      title="Make text italic (_text_)"
                    >
                      <i>I</i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light mr-1"
                      onClick={() => handleTextFormat('strikethrough')}
                      title="Make text strikethrough (~text~)"
                    >
                      <s>S</s>
                    </button>
                    <button className="btn btn-sm btn-light mr-1">
                      <i>&lt;/&gt;</i>
                    </button>
                    <button onClick={handleAddVariable} className="btn btn-sm btn-light">
                      + Add variable
                    </button>
                  </div>
                  {/* Variable Fields */}
                  {template.category !== 'AUTHENTICATION' && bodyVariables.length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-dark mb-3">Variables</h6>
                      {bodyVariables.map((variable, index) => (
                        <div className="form-group d-flex align-items-center gap-2" key={index}>
                          <label className="mb-0">{variable}</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Enter example value`}
                            value={bodyVariableEntries[index] || ''}
                            onChange={(e) => handleBodyVariableInputChange(index, e.target.value)}
                          />
                          <button
                            className="btn btn-danger"
                            onClick={() => handleRemoveVariable(index)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="form-group">
                  <label className="d-flex justify-content-between">
                    <span>
                      Footer <span className="text-muted">• Optional</span>
                    </span>
                    <small className="text-muted">{templateFooter.length}/60</small>
                  </label>
                  <div className="row px-3 gap-2">
                    {template.category === 'AUTHENTICATION' && (
                      <input
                        type="number"
                        className="form-control col"
                        value={codeExp}
                        min={0}
                        onChange={(e) => setCodeExp(e.target.value)}
                      />
                    )}
                    <input
                      type="text"
                      className="form-control col"
                      placeholder="Enter footer text"
                      disabled={template.category === 'AUTHENTICATION'}
                      value={templateFooter}
                      onChange={(e) => setTemplateFooter(e.target.value)}
                    />
                  </div>
                </div>

                {/* Buttons Section */}
                {templateButtons.length > 0 && (
                  <div className="form-group">
                    <label className="d-flex justify-content-between">
                      <span>Buttons</span>
                    </label>
                    {templateButtons.map((button, index) => (
                      <div key={index} className="row px-3 gap-2 align-items-center">
                        <input
                          type="text"
                          className="form-control col"
                          placeholder="Copy code text"
                          value={button.text}
                          onChange={(e) => handleButtonTextChange(index, e.target.value)}
                        />
                        <small className="text-muted">{button.text.length}/25</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between mt-4 mb-4">
                <Button
                  as={Link}
                  variant="ghost"
                  to={`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`}
                >
                  Discard
                </Button>
                <Button variant="primary" onClick={() => handleSubmit()}>
                  Submit for Review
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Preview Panel */}
        <div className="col-md-4">
          <WhatsAppTemplatePreview
            header={templateHeader}
            body={templateBody}
            footer={templateFooter}
            buttons={templateButtons}
          />
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplateEditor;
